import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import Seo from "../components/Seo";
import Brand from "../components/Brand";

export default function Register() {
  const { register, loading } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name:"", email: "", password: "", role: "student", grade_level: "elementary" });

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      await register(form);
      toast.success("Welcome to WriterSpark!");
      nav("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    }
  };

  const Pill = ({ field, value, children }) => (
    <button type="button"
      data-testid={`pill-${field}-${value}`}
      onClick={()=>setForm({...form, [field]: value})}
      className={`rounded-full px-4 py-2 text-sm font-bold border-2 transition ${form[field]===value ? 'bg-indigo-600 text-white border-indigo-700' : 'bg-white border-slate-200 text-slate-700 hover:border-indigo-300'}`}
    >{children}</button>
  );

  return (
    <div className="min-h-screen bg-mesh">
      <Seo
        title="Sign Up Free | WriterSpark — Student Writing Coach"
        description="Create a free WriterSpark account and start improving your writing today — grammar drills, essay coaching, SAT/AP prep, and daily prompts for middle and high school students."
        keywords="sign up writerspark, free student writing account, essay coach signup, writing improvement registration"
        path="/register"
      />
      <div className="max-w-6xl mx-auto px-6 py-10 grid lg:grid-cols-[1fr_440px] gap-12 items-start">
        {/* SEO content (crawlable) */}
        <section className="pt-4">
          <Brand to="/" tone="light" size="lg" />
          <h1 className="mt-8 text-4xl sm:text-5xl font-bold leading-tight" style={{ fontFamily: "Outfit" }}>
            Sign up free and start <span className="text-violet-600">writing better</span> today.
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-xl">
            WriterSpark is the AI writing coach built for middle and high school students. Get rubric-aligned feedback on essays, daily writing prompts, grammar drills, and SAT/AP timed practice — all in 20-30 focused minutes a day.
          </p>
          <ul className="mt-6 space-y-3 text-sm text-slate-700 max-w-md">
            <li className="flex items-start gap-2"><span className="text-indigo-600 font-bold">✓</span> 5 free AI coaching sessions every day</li>
            <li className="flex items-start gap-2"><span className="text-indigo-600 font-bold">✓</span> Daily writing prompts + timed sprints</li>
            <li className="flex items-start gap-2"><span className="text-indigo-600 font-bold">✓</span> Grammar drills with instant explanations</li>
            <li className="flex items-start gap-2"><span className="text-indigo-600 font-bold">✓</span> Rubric scoring for every essay</li>
            <li className="flex items-start gap-2"><span className="text-indigo-600 font-bold">✓</span> Streaks, badges, and growth analytics</li>
          </ul>
          <p className="mt-6 text-sm text-slate-500 max-w-md">
            No credit card required. Cancel anytime. Parents and teachers can also create an account to link their students and see real writing growth.
          </p>
        </section>

        <div className="w-full tactile-card p-8 sm:p-10 lg:sticky lg:top-6">
          <h2 className="text-2xl font-bold mb-2" style={{fontFamily:'Outfit'}}>Create your account</h2>
          <p className="text-slate-600 mb-6 text-sm">A coach for every kind of writer.</p>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="text-xs uppercase font-bold tracking-widest text-slate-500">I am a...</label>
              <div className="flex gap-2 mt-2 flex-wrap">
                <Pill field="role" value="student">Student</Pill>
                <Pill field="role" value="parent">Parent</Pill>
                <Pill field="role" value="teacher">Teacher</Pill>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase font-bold tracking-widest text-slate-500">Grade level</label>
              <div className="flex gap-2 mt-2 flex-wrap">
                <Pill field="grade_level" value="elementary">Elementary</Pill>
                <Pill field="grade_level" value="middle">Middle</Pill>
                <Pill field="grade_level" value="high">High</Pill>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase font-bold tracking-widest text-slate-500">Name</label>
              <input data-testid="register-name" required value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})}
                className="mt-1 w-full rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-400 px-4 py-3 outline-none" />
            </div>
            <div>
              <label className="text-xs uppercase font-bold tracking-widest text-slate-500">Email</label>
              <input data-testid="register-email" type="email" required value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})}
                className="mt-1 w-full rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-400 px-4 py-3 outline-none" />
            </div>
            <div>
              <label className="text-xs uppercase font-bold tracking-widest text-slate-500">Password</label>
              <input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={(e)=>setForm({...form, password:e.target.value})}
                className="mt-1 w-full rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-400 px-4 py-3 outline-none" />
            </div>
            <button data-testid="register-submit" disabled={loading} type="submit" className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-full py-3.5 tactile-shadow disabled:opacity-60">
              {loading ? "Creating..." : "Create account"}
            </button>
          </form>
          <p className="text-center text-sm text-slate-600 mt-6">
            Already have an account? <Link to="/login" data-testid="link-login" className="text-indigo-700 font-bold">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
